import {
  cloneDeep,
  get,
  isNil,
  isPlainObject,
  round,
  set,
  sortBy,
  uniq,
} from "lodash";
import { CONTRIBUTION_STRUCTURE_MEMBERS } from "utils/constants";

const PERCENT_FACTOR = 100;
const PERCENT_PRECISION = 5;
const FRACTION_PRECISION = 7;
const DEPARTMENT_CODE_LENGTH = 3;

export const GRID_KEY = "grilles";
export const REINFORCEMENTS_KEY = "renforts";
export const CONSTANTS_KEY = "constantes";
export const ZONIER_KEY = "zonier_departements";
export const BRACKETS_KEY = "tranches_age";

export const toPercent = (fraction) =>
  isNil(fraction) || fraction === ""
    ? null
    : round(Number(fraction) * PERCENT_FACTOR, PERCENT_PRECISION);

export const fromPercent = (percent) =>
  isNil(percent) || percent === ""
    ? null
    : round(Number(percent) / PERCENT_FACTOR, FRACTION_PRECISION);

export const withValue = (prices, path, value) => {
  const next = cloneDeep(prices || {});
  set(next, path, value);
  return next;
};

export const collectiveSections = (prices) => ({
  constants: isPlainObject(get(prices, CONSTANTS_KEY)),
  grid: isPlainObject(get(prices, GRID_KEY)),
  reinforcements: isPlainObject(get(prices, REINFORCEMENTS_KEY)),
  zonier: isPlainObject(get(prices, ZONIER_KEY)),
});

export const isCollectivePrices = (prices) =>
  Object.values(collectiveSections(prices)).some(Boolean);

export const constantEntries = (prices) =>
  Object.entries(get(prices, CONSTANTS_KEY) || {}).filter(
    ([, value]) => typeof value === "number",
  );

const keysOf = (value) => (isPlainObject(value) ? Object.keys(value) : []);

export const orderedMembers = (structure, members) => {
  const known = get(CONTRIBUTION_STRUCTURE_MEMBERS, structure, []);
  return [
    ...known.filter((member) => members.includes(member)),
    ...members.filter((member) => !known.includes(member)),
  ];
};

export const gridStructures = (prices) => keysOf(get(prices, GRID_KEY));

export const gridCategories = (prices, structure) =>
  keysOf(get(prices, [GRID_KEY, structure]));

export const gridZones = (prices, structure, category) =>
  keysOf(get(prices, [GRID_KEY, structure, category]));

export const gridBrackets = (prices, structure, category) => {
  const zones = get(prices, [GRID_KEY, structure, category]) || {};
  const present = uniq(Object.values(zones).flatMap(keysOf));
  const declared = get(prices, BRACKETS_KEY) || [];
  return [
    ...declared.filter((bracket) => present.includes(bracket)),
    ...present.filter((bracket) => !declared.includes(bracket)),
  ];
};

const gridRows = (prices, structure, category) =>
  Object.values(get(prices, [GRID_KEY, structure, category]) || {}).flatMap(
    (brackets) => Object.values(brackets || {}),
  );

export const gridMembers = (prices, structure, category) =>
  orderedMembers(
    structure,
    uniq(gridRows(prices, structure, category).flatMap(keysOf)),
  );

export const gridLevelCount = (prices, structure, category) =>
  Math.max(
    0,
    ...gridRows(prices, structure, category).flatMap((row) =>
      Object.values(row || {})
        .filter(Array.isArray)
        .map((levels) => levels.length),
    ),
  );

export const gridCellPath = ({
  structure,
  category,
  zone,
  bracket,
  member,
  levelIndex,
}) => [
  GRID_KEY,
  structure,
  category,
  zone,
  bracket,
  member,
  ...(isNil(levelIndex) ? [] : [levelIndex]),
];

export const reinforcementRows = (prices) =>
  keysOf(get(prices, REINFORCEMENTS_KEY)).flatMap((structure) =>
    orderedMembers(
      structure,
      keysOf(get(prices, [REINFORCEMENTS_KEY, structure])),
    ).map((member) => ({ structure, member })),
  );

export const reinforcementCodes = (prices) =>
  uniq(
    reinforcementRows(prices).flatMap(({ structure, member }) =>
      keysOf(get(prices, [REINFORCEMENTS_KEY, structure, member])),
    ),
  );

export const reinforcementCellPath = ({ structure, member, code }) => [
  REINFORCEMENTS_KEY,
  structure,
  member,
  ...(isNil(code) ? [] : [code]),
];

export const zonierRows = (prices) =>
  Object.entries(get(prices, ZONIER_KEY) || {})
    .map(([department, entry]) => ({
      department,
      name: get(entry, "nom", ""),
      zone: get(entry, "zone", null),
    }))
    .sort((left, right) =>
      left.department
        .padStart(DEPARTMENT_CODE_LENGTH, "0")
        .localeCompare(right.department.padStart(DEPARTMENT_CODE_LENGTH, "0")),
    );

const byOrder = (rows) =>
  sortBy(rows || [], (row) => Number(get(row, "order")) || 0);

export const planBrackets = (axes) =>
  byOrder(get(axes, "ageBrackets"))
    .map(({ name }) => name)
    .filter(Boolean);

export const planColumns = (axes) =>
  [
    ...byOrder(get(axes, "zones")).map(({ name }) => ({
      key: name,
      label: name,
    })),
    ...byOrder(get(axes, "regimes"))
      .slice(1)
      .map(({ code, name }) => ({ key: code, label: name || code })),
  ].filter(({ key }) => Boolean(key));

export const usesPlanAxes = (axes) =>
  planBrackets(axes).length > 0 && planColumns(axes).length > 0;

export const hasRegimeColumns = (axes) =>
  (get(axes, "regimes") || []).length > 1;

export const orphanGridKeys = (prices, structure, category, axes) => {
  const columns = planColumns(axes).map(({ key }) => key);
  const brackets = planBrackets(axes);
  return uniq([
    ...gridZones(prices, structure, category).filter(
      (zone) => !columns.includes(zone),
    ),
    ...gridBrackets(prices, structure, category).filter(
      (bracket) => !brackets.includes(bracket),
    ),
  ]);
};

export const zoneChoices = (prices) =>
  uniq([
    ...zonierRows(prices)
      .map(({ zone }) => zone)
      .filter((zone) => !isNil(zone)),
    ...gridStructures(prices).flatMap((structure) =>
      gridCategories(prices, structure).flatMap((category) =>
        gridZones(prices, structure, category),
      ),
    ),
  ]).sort();
